import React from 'react';
import { Reorder, useDragControls } from 'motion/react';
import { GripVertical, Trash2 } from 'lucide-react';
import { AddRowButton, AutoTextarea, IconButton, TextInput } from '../editor/fields';
import { CaseStudyResult, generateId } from './types';

// Result cards: title, description and an optional Lucide icon name.

interface Props {
  results: CaseStudyResult[];
  onChange: (results: CaseStudyResult[]) => void;
}

export const ResultsSection: React.FC<Props> = ({ results, onChange }) => {
  const update = (id: string, patch: Partial<CaseStudyResult>) => onChange(results.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const remove = (id: string) => onChange(results.filter((r) => r.id !== id));
  const add = () => onChange([...results, { id: generateId(), title: '', description: '', iconName: '', sortOrder: results.length }]);

  return (
    <div>
      {results.length ? (
        <Reorder.Group as="div" axis="y" values={results} onReorder={onChange} className="space-y-3">
          {results.map((result) => (
            <ResultRow key={result.id} result={result} onChange={(patch) => update(result.id, patch)} onDelete={() => remove(result.id)} />
          ))}
        </Reorder.Group>
      ) : (
        <p className="text-[12.5px] text-[color:var(--oa-muted)]">No results yet.</p>
      )}
      <div className="mt-3">
        <AddRowButton onClick={add}>Add result</AddRowButton>
      </div>
    </div>
  );
};

const ResultRow: React.FC<{ result: CaseStudyResult; onChange: (patch: Partial<CaseStudyResult>) => void; onDelete: () => void }> = ({ result, onChange, onDelete }) => {
  const controls = useDragControls();
  return (
    <Reorder.Item as="div" value={result} dragListener={false} dragControls={controls} className="group/row rounded-xl border border-[color:var(--oa-line-2)] bg-[color:var(--oa-card)] p-3">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onPointerDown={(e) => controls.start(e)}
          className="grid h-6 w-5 cursor-grab touch-none place-items-center rounded text-[color:var(--oa-faint)] hover:bg-[color:var(--oa-raised-2)] hover:text-[color:var(--oa-muted)] active:cursor-grabbing"
          aria-label="Drag to reorder"
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <input
          value={result.title}
          onChange={(e) => onChange({ title: e.target.value })}
          placeholder="Result title"
          className="min-w-0 flex-1 bg-transparent text-[14px] font-semibold text-[color:var(--oa-text)] outline-none placeholder:text-[color:var(--oa-faint)]"
        />
        <IconButton tone="danger" onClick={onDelete} aria-label="Remove result" className="opacity-0 transition-opacity group-hover/row:opacity-100">
          <Trash2 className="h-3.5 w-3.5" />
        </IconButton>
      </div>
      <AutoTextarea bare minRows={2} value={result.description} onChange={(e) => onChange({ description: e.target.value })} placeholder="Describe this result" className="mt-1.5 pl-7 text-[13px] leading-6 text-[color:var(--oa-muted)]" />
      <div className="mt-2 w-44 pl-7">
        <TextInput value={result.iconName || ''} onChange={(e) => onChange({ iconName: e.target.value })} placeholder="Icon, e.g. TrendingUp" className="h-7 text-[12px] text-[color:var(--oa-muted)]" />
      </div>
    </Reorder.Item>
  );
};
